export const translations = {
  en: {
    loading: 'Loading...',
    navbar: {
      welcome: (name) => `Welcome, ${name}`,
      reportsUsed: (used, limit) => `${used} of ${limit} reports used`,
      resetsOn: (date) => `Your free reports reset on ${date}`,
      myReports: 'My Reports',
      upgrade: 'Upgrade',
      billing: 'Billing',
      logOut: 'Log out',
      deleteAccount: 'Delete account',
      deleteAccountConfirm: 'Permanently delete your account and profile data? This cannot be undone.',
      deleteAccountConfirmPro: 'Permanently delete your account and profile data? Your paid subscription will be cancelled immediately with no refund for unused time. This cannot be undone.',
    },
    signin: {
      title: 'Sign In',
      continueWithGoogle: 'Continue with Google',
      or: 'Or',
      emailPlaceholder: 'Email',
      passwordPlaceholder: 'Password',
      signInWithEmail: 'Sign In with Email',
      forgotPassword: 'Forgot Password?',
      noAccount: "Don't have an account?",
      signUp: 'Sign Up',
      noAccountExists: 'No account exists with this email.',
      clickToCreate: 'Click here to create account',
      googleNote: 'Continue with Google may not work for student accounts.',
      googleNoteEmail: 'Sign in with email instead.',
      resetPasswordSent: 'Password reset email sent! Check your inbox.',
      resetTitle: 'Reset Password',
      resetSubtitle: "Enter your email address and we'll send you a link to reset your password.",
      sendResetEmail: 'Send Reset Email',
      backToSignIn: 'Back to Sign In',
    },
    signup: {
      title: 'Sign Up',
      continueWithGoogle: 'Continue with Google',
      or: 'Or',
      nameLabel: 'Name',
      namePlaceholder: 'Name',
      emailLabel: 'Email',
      emailPlaceholder: 'Email',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Password (min 6 characters)',
      confirmPasswordLabel: 'Confirm Password',
      confirmPasswordPlaceholder: 'Confirm Password',
      roleLabel: 'Role',
      roleSelect: 'Select your role',
      roleEducator: 'Educator',
      roleStudent: 'Student',
      roleOther: 'Other',
      signUpWithEmail: 'Sign Up with Email',
      alreadyHaveAccount: 'Already have an account?',
      signIn: 'Sign In',
      googleNote: 'Continue with Google may not work for student accounts.',
      googleNoteEmail: 'Sign up with email instead.',
      errorName: 'Please enter your name',
      errorRole: 'Please select your role',
      errorPasswordMatch: 'Passwords do not match',
      errorPasswordLength: 'Password must be at least 6 characters long',
      successMessage: 'Account created successfully! Please check your email to verify your account.',
    },
    verifyEmail: {
      title: 'Verify Your Email',
      sentTo: "We've sent a verification email to",
      checkInbox: 'Please check your inbox and click the verification link to activate your account. You may need to check your spam/junk folder.',
      sending: 'Sending...',
      resend: 'Resend Verification Email',
      afterVerify: "After verifying your email, you'll be automatically redirected.",
      sent: 'Verification email sent! Please check your inbox.',
      failedResend: 'Failed to send verification email. Please try again.',
    },
    home: {
      title: 'Welcome to Aidify!',
      refresh: 'You may have to refresh your Google Docs page to see the report.',
      close: 'You may now close this window.',
      contact: 'Contact Us: info@aidify.ca',
    },
    sharedReports: {
      title: 'My Shared Reports',
      loading: 'Loading...',
      error: 'Failed to load shared reports.',
      deleteAll: 'Delete All',
      deleting: 'Deleting…',
      empty: 'You have no shared reports.',
      colDocument: 'Document',
      colCreated: 'Created',
      delete: 'Delete',
      untitled: '(untitled)',
      deleteConfirm: (title) => `Delete the shared report for "${title}"? This cannot be undone.`,
      deleteAllConfirm: (n) => `Delete all ${n} shared report${n !== 1 ? 's' : ''}? This cannot be undone.`,
      deleteAllFailed: (n) => `${n} report(s) could not be deleted.`,
    },
    roleSelection: {
      title: 'Please select a role',
      subtitle: 'Help us personalize your experience by selecting your role',
      errorSelect: 'Please select a role',
      educator: 'Educator',
      student: 'Student',
      other: 'Other',
      continue: 'Continue',
    },
    emailVerification: {
      title: '⚠️ Email Verification Required',
      body: 'Please verify your email address to access all features. Check your inbox for a verification email.',
      resend: 'Resend Verification Email',
      sent: 'Verification email sent! Please check your inbox.',
    },
    sharedReport: {
      loading: 'Loading shared report...',
      errorPrefix: 'Error',
      errors: {
        noShareId: 'No share ID provided',
        missingKey: 'Missing decryption key in URL',
        notFound: 'Shared report not found. Aidify report link either expired or was deleted. Please ask the document owner to generate and send a new link.',
        expired: 'This shared report has expired.',
        invalidData: 'Invalid report data: missing fields',
        storageFailed: (status, statusText) => `Storage fetch failed: ${status} ${statusText}`,
        loadFailed: 'Failed to load shared report',
        invalidShareId: 'Invalid share ID',
      },
      replayOfEdits: 'Replay of Edits',
      speed: 'Speed:',
      speedSlow: '1 (slow)',
      speedFast: '11 (fast)',
      prevEdit: 'Previous Edit',
      nextEdit: 'Next Edit',
      play: 'Play',
      pause: 'Pause',
      documentTabs: 'Document tabs',
      deletedTab: 'Deleted Tab',
      documentStatsFor: 'Document Stats For',
      wordCount: 'Word Count',
      deletes: 'Deletes',
      timeSpent: 'Time spent',
      timeSpentTooltip: 'Total time actively typing with no more than a 10-minute break.',
      editsLabel: 'Edits',
      editsTooltip: 'Total changes — characters typed, deletions. Pasting counts as one edit.',
      timeHrMin: (h, m) => `${h} hr ${m} min`,
      viewGroupBreakdown: 'View Group Breakdown',
      anonymous: 'Anonymous',
      anonymousUsers: 'Anonymous Users',
      allUsers: 'All Users',
      unknown: 'Unknown',
      writingReportFor: 'Writing Report For',
      selectUser: 'Select User:',
      copyPasteTitle: 'External Copy/Pasted Text — Click to view location',
      showHyperlinks: 'Show Hyperlinks',
      showAll: 'Show All',
      showLess: 'Show Less',
      editsPerDateTitle: 'Graph of Edits Made per Date For',
      editsPerDateHint: 'Click on a bar to view the time breakdown of edits for that day.',
      editsByHourTitle: 'Graph of Edits Made by Hour For',
      on: 'on',
      allDates: 'All Dates',
      viewForAllDates: 'View for All Dates',
      timeSpentPerDateTitle: 'Graph of Time Spent per Date For',
      writingSessionsFor: 'Writing Sessions For',
      writingSessionsHint: 'A period of writing with no more than a 10 minute break.',
      startTime: 'Start Time:',
      duration: 'Duration:',
      editsCount: 'Edits:',
      seeAll: 'See All',
      hide: 'Hide',
      groupWorkStats: 'Group Work Stats',
      timeSpentOption: 'Time Spent',
      editsMadeOption: 'Edits Made',
      chartDate: 'Date',
      chartNumberOfEdits: 'Number of Edits',
      chartTimeOfDay: 'Time of Day',
      chartTimeSpentMinutes: 'Time Spent (Minutes)',
      tooltipMin: 'min',
      tooltipEdits: 'edits',
      formatDuration: (h, m, s) => `${h} hours, ${m} minutes, ${s} seconds`,
      hourLabel: (hr) => `${hr % 12 || 12} ${hr >= 12 ? 'PM' : 'AM'}`,
      dateLocale: 'en-US',
    },
    pricing: {
      title: 'Aidify Pro',
      info: {
        ariaLabel: 'About Aidify Pro',
        greeting: 'Hi,',
        body1Before:
          'This is Edlyn, co-founder of Aidify. Aidify started as a small project to help our own schools and we never expected to be used so widely. We want to keep it as accessible as possible but do have maintenance and operational fees to pay. If the cost is a barrier to you, please reach out to me at ',
        email: 'edlyn@aidify.ca',
        body1After: ' and we can work something out.',
        body2:
          'We also donate 20% of our profits to support mental health charities, a cause that we care deeply about.',
        closing: 'With gratitude,',
        signature: 'Edlyn To',
        close: 'Close',
      },
      subtitle: 'You get 20 free Aidify Reports every month. Upgrade for unlimited reports.',
      institutionalBefore: 'For institutional pricing, please contact ',
      institutionalEmail: 'edlyn@aidify.ca',
      institutionalOr: ' or fill out ',
      institutionalFormLabel: 'this form',
      institutionalAfter: '.',
      freeUsage: (used, limit) => `You've used ${used} of ${limit} free reports this month.`,
      resetsOn: (date) => `Your free reports reset on ${date}.`,
      proActive: 'You have an active Aidify Pro subscription. If you have any issues or questions, please contact edlyn@aidify.ca',
      nextPayment: (date) => `Next payment on ${date}.`,
      subscriptionCancelled: (date) => `Subscription cancelled, you will not continue being charged. You still have access to Aidify Pro until ${date}.`,
      monthlyName: 'Monthly',
      monthlyPrice: '$5',
      monthlyPeriod: '/month',
      yearlyName: 'Yearly',
      yearlyPrice: '$40',
      yearlyPeriod: '/year',
      yearlyNote: 'Save 33% vs monthly',
      unlimitedReports: 'Unlimited Aidify Reports',
      everythingFree: 'Everything in the free plan',
      cancelAnytime: 'Cancel anytime',
      subscribe: 'Subscribe',
      switchPlan: 'Switch plan',
      confirmSwitch: (name) =>
        `Switch to the ${name} plan? Your subscription updates immediately and billing is prorated.`,
      switchSuccess: 'Plan updated — your subscription has been switched.',
      cancelSubscription: 'Cancel subscription',
      confirmCancel: (date) =>
        `Cancel your subscription? You'll keep Aidify Pro access until ${date}, and you can resume anytime before then.`,
      resumeSubscription: 'Resume subscription',
      confirmResume: 'Resume your subscription? Your Aidify Pro plan will continue and billing will carry on as usual.',
      resumeSuccess: 'Your subscription has been resumed.',
      manageSubscription: 'Manage subscription',
      currentPlan: 'Current plan',
      alreadySubscribedMsg: 'You already have an active subscription. Use Manage subscription to switch plans.',
      successMsg: 'Subscription active — thanks for upgrading!',
      cancelMsg: 'Checkout canceled. You can subscribe anytime.',
      loginRequired: 'Please sign in to subscribe.',
      errorMsg: 'Something went wrong. Please try again.',
      redirecting: 'Redirecting to Stripe…',
    },
  },
  fr: {
    loading: 'Chargement…',
    navbar: {
      welcome: (name) => `Bienvenue, ${name}`,
      reportsUsed: (used, limit) => `${used} de ${limit} rapports utilisés`,
      resetsOn: (date) => `Vos rapports gratuits se réinitialisent le ${date}`,
      myReports: 'Mes rapports',
      upgrade: 'Améliorer',
      billing: 'Facturation',
      logOut: 'Se déconnecter',
      deleteAccount: 'Supprimer le compte',
      deleteAccountConfirm: 'Supprimer définitivement votre compte et les données de profil ? Cette action est irréversible.',
      deleteAccountConfirmPro: 'Supprimer définitivement votre compte et les données de profil ? Votre abonnement payant sera annulé immédiatement, sans remboursement pour le temps non utilisé. Cette action est irréversible.',
    },
    signin: {
      title: 'Connexion',
      continueWithGoogle: 'Continuer avec Google',
      or: 'Ou',
      emailPlaceholder: 'Courriel',
      passwordPlaceholder: 'Mot de passe',
      signInWithEmail: 'Se connecter par courriel',
      forgotPassword: 'Mot de passe oublié ?',
      noAccount: 'Vous n\'avez pas de compte ?',
      signUp: 'S\'inscrire',
      noAccountExists: 'Aucun compte associé à ce courriel.',
      clickToCreate: 'Cliquez ici pour créer un compte',
      googleNote: 'Continuer avec Google peut ne pas fonctionner pour les comptes étudiants.',
      googleNoteEmail: 'Connectez-vous par courriel à la place.',
      resetPasswordSent: 'Courriel de réinitialisation envoyé ! Vérifiez votre boîte de réception.',
      resetTitle: 'Réinitialiser le mot de passe',
      resetSubtitle: 'Entrez votre adresse courriel et nous vous enverrons un lien pour réinitialiser votre mot de passe.',
      sendResetEmail: 'Envoyer le courriel de réinitialisation',
      backToSignIn: 'Retour à la connexion',
    },
    signup: {
      title: 'Inscription',
      continueWithGoogle: 'Continuer avec Google',
      or: 'Ou',
      nameLabel: 'Nom',
      namePlaceholder: 'Nom',
      emailLabel: 'Courriel',
      emailPlaceholder: 'Courriel',
      passwordLabel: 'Mot de passe',
      passwordPlaceholder: 'Mot de passe (min. 6 caractères)',
      confirmPasswordLabel: 'Confirmer le mot de passe',
      confirmPasswordPlaceholder: 'Confirmer le mot de passe',
      roleLabel: 'Rôle',
      roleSelect: 'Sélectionnez votre rôle',
      roleEducator: 'Éducateur',
      roleStudent: 'Étudiant',
      roleOther: 'Autre',
      signUpWithEmail: "S'inscrire par courriel",
      alreadyHaveAccount: 'Vous avez déjà un compte ?',
      signIn: 'Se connecter',
      googleNote: 'Continuer avec Google peut ne pas fonctionner pour les comptes étudiants.',
      googleNoteEmail: 'Inscrivez-vous par courriel à la place.',
      errorName: 'Veuillez entrer votre nom',
      errorRole: 'Veuillez sélectionner votre rôle',
      errorPasswordMatch: 'Les mots de passe ne correspondent pas',
      errorPasswordLength: 'Le mot de passe doit comporter au moins 6 caractères',
      successMessage: 'Compte créé avec succès ! Veuillez vérifier votre courriel pour activer votre compte.',
    },
    verifyEmail: {
      title: 'Vérifiez votre courriel',
      sentTo: 'Nous avons envoyé un courriel de vérification à',
      checkInbox: 'Veuillez vérifier votre boîte de réception et cliquer sur le lien de vérification pour activer votre compte. Vérifiez également le dossier spam/indésirables.',
      sending: 'Envoi…',
      resend: 'Renvoyer le courriel de vérification',
      afterVerify: 'Après la vérification, vous serez automatiquement redirigé.',
      sent: 'Courriel de vérification envoyé ! Vérifiez votre boîte de réception.',
      failedResend: "Échec de l'envoi du courriel de vérification. Veuillez réessayer.",
    },
    home: {
      title: 'Bienvenue sur Aidify !',
      refresh: 'Vous devrez peut-être actualiser votre page Google Docs pour voir le rapport.',
      close: 'Vous pouvez maintenant fermer cette fenêtre.',
      contact: 'Contactez-nous : info@aidify.ca',
    },
    sharedReports: {
      title: 'Mes rapports partagés',
      loading: 'Chargement…',
      error: 'Échec du chargement des rapports partagés.',
      deleteAll: 'Tout supprimer',
      deleting: 'Suppression…',
      empty: 'Vous n\'avez aucun rapport partagé.',
      colDocument: 'Document',
      colCreated: 'Créé le',
      delete: 'Supprimer',
      untitled: '(sans titre)',
      deleteConfirm: (title) => `Supprimer le rapport partagé pour « ${title} » ? Cette action est irréversible.`,
      deleteAllConfirm: (n) => `Supprimer ${n} rapport${n !== 1 ? 's' : ''} partagé${n !== 1 ? 's' : ''} ? Cette action est irréversible.`,
      deleteAllFailed: (n) => `${n} rapport(s) n'ont pas pu être supprimés.`,
    },
    roleSelection: {
      title: 'Veuillez sélectionner un rôle',
      subtitle: 'Aidez-nous à personnaliser votre expérience en sélectionnant votre rôle',
      errorSelect: 'Veuillez sélectionner un rôle',
      educator: 'Éducateur',
      student: 'Étudiant',
      other: 'Autre',
      continue: 'Continuer',
    },
    emailVerification: {
      title: '⚠️ Vérification du courriel requise',
      body: 'Veuillez vérifier votre adresse courriel pour accéder à toutes les fonctionnalités. Vérifiez votre boîte de réception.',
      resend: 'Renvoyer le courriel de vérification',
      sent: 'Courriel de vérification envoyé ! Vérifiez votre boîte de réception.',
    },
    sharedReport: {
      loading: 'Chargement du rapport partagé…',
      errorPrefix: 'Erreur',
      errors: {
        noShareId: 'Aucun identifiant de partage fourni',
        missingKey: 'Clé de déchiffrement manquante dans l\'URL',
        notFound: 'Rapport partagé introuvable. Le lien du rapport Aidify a expiré ou a été supprimé. Veuillez demander au propriétaire du document de générer et d\'envoyer un nouveau lien.',
        expired: 'Ce rapport partagé a expiré.',
        invalidData: 'Données de rapport invalides : champs manquants',
        storageFailed: (status, statusText) => `Échec de la récupération depuis le stockage : ${status} ${statusText}`,
        loadFailed: 'Échec du chargement du rapport partagé',
        invalidShareId: 'Identifiant de partage invalide',
      },
      replayOfEdits: 'Relecture des modifications',
      speed: 'Vitesse :',
      speedSlow: '1 (lent)',
      speedFast: '11 (rapide)',
      prevEdit: 'Modification précédente',
      nextEdit: 'Modification suivante',
      play: 'Lecture',
      pause: 'Pause',
      documentTabs: 'Onglets du document',
      deletedTab: 'Onglet supprimé',
      documentStatsFor: 'Statistiques du document pour',
      wordCount: 'Nombre de mots',
      deletes: 'Suppressions',
      timeSpent: 'Temps passé',
      timeSpentTooltip: 'Temps total de frappe active sans pause de plus de 10 minutes.',
      editsLabel: 'Modifications',
      editsTooltip: 'Toutes les modifications — caractères tapés, suppressions. Un collage compte comme une seule modification.',
      timeHrMin: (h, m) => `${h} h ${m} min`,
      viewGroupBreakdown: 'Voir la répartition par groupe',
      anonymous: 'Anonyme',
      anonymousUsers: 'Utilisateurs anonymes',
      allUsers: 'Tous les utilisateurs',
      unknown: 'Inconnu',
      writingReportFor: 'Rapport d\'écriture pour',
      selectUser: 'Sélectionner un utilisateur :',
      copyPasteTitle: 'Texte copié/collé depuis l\'extérieur — Cliquez pour voir l\'emplacement',
      showHyperlinks: 'Afficher les hyperliens',
      showAll: 'Tout afficher',
      showLess: 'Afficher moins',
      editsPerDateTitle: 'Graphique des modifications par date pour',
      editsPerDateHint: 'Cliquez sur une barre pour voir la répartition des modifications de cette journée.',
      editsByHourTitle: 'Graphique des modifications par heure pour',
      on: 'le',
      allDates: 'Toutes les dates',
      viewForAllDates: 'Voir pour toutes les dates',
      timeSpentPerDateTitle: 'Graphique du temps passé par date pour',
      writingSessionsFor: 'Sessions d\'écriture pour',
      writingSessionsHint: 'Une période d\'écriture sans pause de plus de 10 minutes.',
      startTime: 'Heure de début :',
      duration: 'Durée :',
      editsCount: 'Modifications :',
      seeAll: 'Tout voir',
      hide: 'Masquer',
      groupWorkStats: 'Statistiques de travail en groupe',
      timeSpentOption: 'Temps passé',
      editsMadeOption: 'Modifications effectuées',
      chartDate: 'Date',
      chartNumberOfEdits: 'Nombre de modifications',
      chartTimeOfDay: 'Heure de la journée',
      chartTimeSpentMinutes: 'Temps passé (minutes)',
      tooltipMin: 'min',
      tooltipEdits: 'modifications',
      formatDuration: (h, m, s) => `${h} heures, ${m} minutes, ${s} secondes`,
      hourLabel: (hr) => `${hr} h`,
      dateLocale: 'fr-CA',
    },
    pricing: {
      title: 'Aidify Pro',
      info: {
        ariaLabel: 'À propos d’Aidify Pro',
        greeting: 'Bonjour,',
        body1Before:
          'Ici Edlyn, cofondatrice d’Aidify. Aidify a commencé comme un petit projet pour aider nos propres écoles et nous ne nous attendions pas à ce qu’il soit utilisé aussi largement. Nous voulons le garder aussi accessible que possible, mais nous avons des frais d’entretien et d’exploitation à payer. Si le coût représente un obstacle pour vous, n’hésitez pas à me contacter à ',
        email: 'edlyn@aidify.ca',
        body1After: ' et nous trouverons une solution.',
        body2:
          'Nous versons également 20 % de nos profits pour soutenir des organismes de santé mentale, une cause qui nous tient profondément à cœur.',
        closing: 'Avec gratitude,',
        signature: 'Edlyn To',
        close: 'Fermer',
      },
      subtitle: 'Vous obtenez 20 rapports Aidify gratuits chaque mois. Passez à la version supérieure pour des rapports illimités.',
      institutionalBefore: 'Pour la tarification institutionnelle, veuillez contacter ',
      institutionalEmail: 'edlyn@aidify.ca',
      institutionalOr: ' ou remplir ',
      institutionalFormLabel: 'ce formulaire',
      institutionalAfter: '.',
      freeUsage: (used, limit) => `Vous avez utilisé ${used} de ${limit} rapports gratuits ce mois-ci.`,
      resetsOn: (date) => `Vos rapports gratuits se réinitialisent le ${date}.`,
      proActive: 'Vous avez un abonnement Aidify Pro actif. Pour toute question ou problème, veuillez contacter edlyn@aidify.ca',
      nextPayment: (date) => `Prochain paiement le ${date}.`,
      subscriptionCancelled: (date) => `Abonnement annulé, vous ne serez plus facturé. Vous avez toujours accès à Aidify Pro jusqu’au ${date}.`,
      monthlyName: 'Mensuel',
      monthlyPrice: '5 $',
      monthlyPeriod: '/mois',
      yearlyName: 'Annuel',
      yearlyPrice: '40 $',
      yearlyPeriod: '/an',
      yearlyNote: 'Économisez 33 % par rapport au mensuel',
      unlimitedReports: 'Rapports Aidify illimités',
      everythingFree: 'Tout ce qui est inclus dans le forfait gratuit',
      cancelAnytime: 'Annulez à tout moment',
      subscribe: 'S’abonner',
      switchPlan: 'Changer de forfait',
      confirmSwitch: (name) =>
        `Passer au forfait ${name} ? Votre abonnement est mis à jour immédiatement et la facturation est calculée au prorata.`,
      switchSuccess: 'Forfait mis à jour — votre abonnement a été changé.',
      cancelSubscription: 'Annuler l’abonnement',
      confirmCancel: (date) =>
        `Annuler votre abonnement ? Vous conserverez l’accès à Aidify Pro jusqu’au ${date}, et vous pouvez le reprendre à tout moment avant cette date.`,
      resumeSubscription: 'Reprendre l’abonnement',
      confirmResume: 'Reprendre votre abonnement ? Votre forfait Aidify Pro se poursuivra et la facturation continuera normalement.',
      resumeSuccess: 'Votre abonnement a été repris.',
      manageSubscription: 'Gérer l’abonnement',
      currentPlan: 'Forfait actuel',
      alreadySubscribedMsg: 'Vous avez déjà un abonnement actif. Utilisez « Gérer l’abonnement » pour changer de forfait.',
      successMsg: 'Abonnement actif — merci pour votre soutien !',
      cancelMsg: 'Paiement annulé. Vous pouvez vous abonner à tout moment.',
      loginRequired: 'Veuillez vous connecter pour vous abonner.',
      errorMsg: 'Une erreur est survenue. Veuillez réessayer.',
      redirecting: 'Redirection vers Stripe…',
    },
  },
};
